import React from 'react';
import { StudyGuideCard } from '../study-guide-card';
import flag from './img/flag.svg';
import trophy from './img/trophy.svg';
import relaxation from './img/relaxation.svg';
import { TIAResults } from '@/components/custom/tia';
import moment from 'moment';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import {
  GuideTimeline,
  GuideTimelineBody,
  GuideTimelineCards,
  GuideTimelineContent,
  GuideTimelineDate,
  GuideTimelineDay,
  GuideTimelineFooter,
  GuideTimelineHeader,
  GuideTimelineIcon,
  GuideTimelineImg,
  GuideTimelineText,
  GuideTimelineTitle
} from '@/components/custom/guide';

export function StudyGuideTimeline({ studyGuide }) {
  const playful = usePlayfulStudent();
  const daysOfWeek = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];

  const getMotivationalMessage = dayOfWeek => {
    switch (dayOfWeek) {
      case 'Segunda':
        return 'Vamos começar mais uma semana com todo o gás!';
      case 'Terça':
      case 'Quarta':
      case 'Quinta':
        return 'Continue! Seu esforço não é em vão.';
      case 'Sexta':
        return 'Falta pouco para concluir essa semana de estudos. Vamos lá!';
      default:
        return 'Vamos começar mais um dia com todo o gás';
    }
  };

  const tiaResultsDate = moment(studyGuide?.['daily-message']?.['created-at']).format('YYYY-MM-DD');
  const tiaResultsMessage = studyGuide?.['daily-message']?.body;

  const videosByDate = studyGuide['study-plan-videos'].reduce((acc, video) => {
    const formattedDate = moment(video.date).format('YYYY-MM-DD');
    if (!acc[formattedDate]) {
      acc[formattedDate] = [];
    }
    acc[formattedDate].push(video);
    return acc;
  }, {});

  const sortedDates = Object.keys(videosByDate).sort();
  let firstUndoneFound = false;

  if (playful) {
    return (
      <div className="flex flex-col gap-6">
        {sortedDates.map((date, index) => {
          const currentDate = moment(date).format('DD [de] MMMM [de] YYYY');
          const dayOfWeek = daysOfWeek[index % 5];
          const today = moment().format('YYYY-MM-DD');

          return (
            <div
              key={index}
              id={dayOfWeek.toLowerCase()}
              className="scroll-mt-20 rounded-[24px] border border-neutral-lightest bg-[var(--color-bg-cream)] p-5 shadow-card"
            >
              <div className="mb-4 flex items-center gap-3">
                <img
                  src={flag}
                  alt=""
                  className="h-8 w-8 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm font-extrabold leading-snug"
                    style={{ color: '#222731' }}
                  >
                    {getMotivationalMessage(dayOfWeek)}
                  </p>
                  <p className="text-xs font-semibold text-neutral-500">
                    {dayOfWeek}, {currentDate}
                  </p>
                </div>
              </div>

              {tiaResultsDate === date && dayOfWeek !== 'Segunda' && tiaResultsMessage && (
                <div className="mb-4">
                  <TIAResults message={tiaResultsMessage} />
                </div>
              )}

              <div className="flex flex-col gap-3">
                {videosByDate[date].map((video, idx) => {
                  const nextToBeDone = !firstUndoneFound && video.done === false;
                  if (nextToBeDone) {
                    firstUndoneFound = true;
                  }
                  return (
                    <StudyGuideCard
                      key={idx}
                      done={video.done}
                      title={video.taxonomy.name}
                      video={video}
                      guide_index={idx}
                      canBeClicked={date <= today}
                      isCurrent={nextToBeDone}
                    />
                  );
                })}
              </div>

              <div className="mt-4 flex items-center gap-2 border-t border-neutral-lightest pt-4">
                <img
                  src={trophy}
                  alt=""
                  className="h-6 w-6 shrink-0"
                />
                <p className="text-xs font-bold text-neutral-500">{index < 4 ? `Chegamos ao final da ${dayOfWeek.toLowerCase()}-feira.` : 'Parabéns. Você finalizou mais uma semana'}</p>
              </div>
            </div>
          );
        })}

        <div className="flex flex-col items-center gap-4 rounded-[24px] border border-neutral-lightest bg-[var(--color-bg-lilac)] p-8 text-center shadow-card sm:flex-row sm:text-left">
          <div className="min-w-0 flex-1">
            <p
              className="text-base font-extrabold"
              style={{ color: '#222731' }}
            >
              Oba, o final de semana chegou 🎉
            </p>
            <p className="mt-1 text-sm text-neutral-500">
              Aproveite para recarregar as energias.
              <br /> Nos vemos na próxima semana!
            </p>
          </div>
          <img
            src={relaxation}
            alt=""
            className="h-24 w-24 shrink-0"
          />
        </div>
      </div>
    );
  }

  return (
    <GuideTimeline>
      {sortedDates.map((date, index) => {
        const currentDate = moment(date).format('DD [de] MMMM [de] YYYY');
        const dayOfWeek = daysOfWeek[index % 5];
        const today = moment().format('YYYY-MM-DD');

        return (
          <GuideTimelineDay key={index}>
            <GuideTimelineHeader id={dayOfWeek.toLowerCase()}>
              <GuideTimelineIcon
                src={flag}
                alt="bandeira"
              />
              <GuideTimelineTitle>{getMotivationalMessage(dayOfWeek)}</GuideTimelineTitle>
              <GuideTimelineDate>{`${dayOfWeek}, ${currentDate}`}</GuideTimelineDate>
            </GuideTimelineHeader>

            <GuideTimelineCards>
              {tiaResultsDate === date && dayOfWeek !== 'Segunda' && (
                <>
                  {tiaResultsMessage && <TIAResults message={tiaResultsMessage} />}
                  <br />
                  <br />
                </>
              )}

              {videosByDate[date].map((video, idx) => {
                const nextToBeDone = !firstUndoneFound && video.done === false;
                if (nextToBeDone) {
                  firstUndoneFound = true;
                }
                return (
                  <StudyGuideCard
                    key={idx}
                    done={video.done}
                    title={video.taxonomy.name}
                    video={video}
                    guide_index={idx}
                    canBeClicked={date <= today}
                    isCurrent={nextToBeDone}
                  />
                );
              })}
            </GuideTimelineCards>

            <GuideTimelineFooter>
              <GuideTimelineIcon
                src={trophy}
                alt="troféu"
              />
              {index < 4 ? <GuideTimelineTitle>Chegamos ao final da {dayOfWeek.toLowerCase()}-feira.</GuideTimelineTitle> : <GuideTimelineTitle>Parabéns. Você finalizou mais uma semana</GuideTimelineTitle>}
            </GuideTimelineFooter>
          </GuideTimelineDay>
        );
      })}

      <GuideTimelineDay isPause>
        <GuideTimelineBody>
          <GuideTimelineContent>
            <GuideTimelineTitle>Oba, o final de semana chegou</GuideTimelineTitle>
            <GuideTimelineText>
              Aproveite para recarregar as energias.
              <br /> Nos vemos na próxima semana!
            </GuideTimelineText>
          </GuideTimelineContent>
          <GuideTimelineImg
            src={relaxation}
            alt="relaxamento"
          />
        </GuideTimelineBody>
      </GuideTimelineDay>
    </GuideTimeline>
  );
}
